import { useEffect, useRef, useState } from 'react';
import { trpc } from '../lib/api/trpc';

type TaskStatus = 'pending' | 'processing' | 'completed' | 'failed';

interface UseTaskPollingOptions {
  taskId: string | null;
  onCompleted?: (output: any) => void;
  onFailed?: (error: string) => void;
  interval?: number;
  enabled?: boolean;
}

export function useTaskPolling({
  taskId,
  onCompleted,
  onFailed,
  interval = 2000,
  enabled = true,
}: UseTaskPollingOptions) {
  const [status, setStatus] = useState<TaskStatus | null>(null);
  const [output, setOutput] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const completedRef = useRef(false);

  const { refetch } = trpc.tasks.getById.useQuery(
    { taskId: taskId! },
    {
      enabled: false, // We'll manually trigger this
    },
  );

  useEffect(() => {
    if (!taskId || !enabled || completedRef.current) {
      return;
    }

    const pollTask = async () => {
      try {
        const result = await refetch();

        if (result.data) {
          setStatus(result.data.status as TaskStatus);
          setOutput(result.data.output);
          setError(result.data.error);

          if (result.data.status === 'completed') {
            completedRef.current = true;
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
            }
            onCompleted?.(result.data.output);
          } else if (result.data.status === 'failed') {
            completedRef.current = true;
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
            }
            onFailed?.(result.data.error || 'Task failed');
          }
        }
      } catch (error) {
        console.error('Error polling task:', error);
      }
    };

    // Initial poll
    pollTask();

    // Set up interval
    intervalRef.current = setInterval(pollTask, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [taskId, enabled, interval, onCompleted, onFailed, refetch]);

  return {
    status,
    output,
    error,
    isPolling: !!taskId && !completedRef.current && enabled,
  };
}

